import React, { useState, forwardRef, useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import classNames from 'classnames';
import {
    closestCenter,
    DndContext, 
    DragOverlay,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
  } from '@dnd-kit/core';
  import {
    useSortable,
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
  } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import withAuthProvider from '../../hoc/withAuthProvider';
import TogglePins from './toggle-pins';
import Import from './import';
import EditTakeover from './edit-takeover';
import SortButton from './sort-button';
import Takeover from '../takeover';
import { Button } from '../forms';
import ColorPicker from './color-picker';
import SaveButton from '../report/save-button';
import SpinnerDots from '../spinner/dots';
import { suggestColor } from './helpers/ui/suggest-color';
import { createMapService } from '../../services/mapService';
import { REACT_APP_API_BASE} from '../../config';
import './create-district.scss';

const Item = forwardRef((props, ref) => {
    const { children, district, type, sortOrder, handleTogglePins, scenarios, style, dataIsLoading, ...rest } = props;

    return (
        <tr ref={ref} style={style} className="table__row-sortable">
            <td>
                {sortOrder.key === 'sortorder' &&
                    <>
                        {dataIsLoading &&
                            <button className="table__row-sort-trigger" disabled={true}>
                                <svg focusable="false" viewBox="0 0 24 24" aria-hidden="true" draggable="true"><path d="M11 18c0 1.1-.9 2-2 2s-2-.9-2-2 .9-2 2-2 2 .9 2 2zm-2-8c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0-6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm6 4c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"></path></svg>
                            </button>
                        }
                        {!dataIsLoading &&
                            <button {...rest} className="table__row-sort-trigger" disabled={dataIsLoading}>
                                <svg focusable="false" viewBox="0 0 24 24" aria-hidden="true" draggable="true"><path d="M11 18c0 1.1-.9 2-2 2s-2-.9-2-2 .9-2 2-2 2 .9 2 2zm-2-8c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0-6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm6 4c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"></path></svg>
                            </button>
                        }
                    </>
                }
            </td>
            <td className="table__text">
                <Link className="districts__district-link" to={`${type === 'foodservice' ? `/foodservice-scenario` : `/scenario`}/${district.salesDistrictScenarioId}/district/${district.id}`}>
                    <span className="districts__district-color" style={{backgroundColor: district.color}}></span>{district.name}
                </Link>
            </td>
            <td>{district.numberOfHits.toLocaleString('sv')}</td>
            <td>
                <TogglePins checked={district.showPins} onChange={(checked) => handleTogglePins(checked)} />
            </td>
            <td>{district.salesDistrictArea.toLocaleString('sv')}</td>
            {type !== 'foodservice' &&
                <>
                    <td>{district.turnOver.toLocaleString('sv')}</td>
                    <td>{district.frequency.toLocaleString('sv')}</td>
                    <td>{district.vistingTimeHours.toLocaleString('sv')}</td>
                </>
            }
            <td className="table__text">{new Date(district.lastUpdated).toLocaleDateString('sv')}</td>
            <td>
                <EditTakeover id={district.id} 
                            isActive={district.isActive} 
                            createUrl={`${type === 'foodservice' ? `/foodservice-scenario` : `/scenario`}/${district.salesDistrictScenarioId}/create`}
                            listUrl={type === 'foodservice' ? '/foodservice-scenario' : '/scenario'}
                            isDistrictEdit={true} 
                            confirmText="Vill du ta bort distriktet permanent?" 
                            editButton="Redigera säljdistrikt" 
                            deleteHeading="TA BORT DISTRIKT" 
                            deleteButtonText="Ta bort distrikt" 
                            scenarioId={district.salesDistrictScenarioId}
                            scenarios={scenarios} 
                            districtName={district.name} />
            </td>
            {children}
        </tr>
    );                                                      
});

const SortableItem = props => { 
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
    } = useSortable({
        id: props.id,
        disabled: props.dataIsLoading
    });
  
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <Item ref={setNodeRef} {...props} style={style} {...attributes} {...listeners} />
  );
}

const EMPTY_STATE = {
    emptyDistrict: null,
    isSaving: false,
    isSaved: false,
    isSaveDialogOpen: false,
    isLoading: false,
    color: '#ff0000',
    textColor: '#ff0000',
    addWatcher: false,
    name: ''
};

const ActiveDistricts = ({fetchDistricts, ...props}) => {
    const params = useParams();
    const mapService = createMapService(props.fetch);

    const [activeDistricts, setActiveDistricts] = useState(props.activeDistricts);

    const [sortOrder, setSortOrder] = useState({
        key: 'sortorder',
        direction: 'asc'
    }); 

    const [activeId, setActiveId] = useState(null);

    const [saveDialogState, setSaveDialogState] = useState({
        ...EMPTY_STATE
    });

    const isMountedRef = useRef(true);
    const isSortSavingRef = useRef(false);

    useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    useEffect(() => {
        if (props.activeDistricts) {
            setActiveDistricts(props.activeDistricts);
        }
    }, [props.activeDistricts]);

    const getTokenHeaders = async () => {
        const token = await props.getAccessToken();
        const headers = new Headers();
        const bearer = `Bearer ${token}`;
        headers.append('Authorization', bearer);
        headers.append('Accept', 'application/json');
        headers.append('Content-Type', 'application/json');
        return headers;
    }

    const handleDragStart = (event) => {
        const { active } = event;
        setActiveId(active.id);
    };
      
    const handleDragEnd = async (event) => {
        const { active, over } = event;
        setActiveId(null);

        if (isSortSavingRef.current) {
            return;
        }

        if (active.id !== over.id && over) {
            isSortSavingRef.current = true;
            props.setDataIsLoading(true);

            const setNewSortOrder = async (changedDistrict, sortOrder) => {
                try {
                    const headers = await getTokenHeaders();
                    const saveResponse = await fetch(`${REACT_APP_API_BASE}/salesdistrict/${props.type}/scenario/${changedDistrict.salesDistrictScenarioId}/district/${changedDistrict.id}/sortorder/${sortOrder}`, {
                        method: 'POST',
                        headers: headers
                    });

                    if (saveResponse.ok) {
                        const jsonRes = await saveResponse.json();
                        setActiveDistricts(jsonRes.active);
                    }
                } finally {
                    isSortSavingRef.current = false;
                    props.setDataIsLoading(false);
                }
            };

            const fromIndex = activeDistricts.findIndex(item => item && item.id === active.id);
            const toIndex = activeDistricts.findIndex(item => item && item.id === over.id);
            const districtDragged = activeDistricts[fromIndex];
            const toDistrict = activeDistricts[toIndex];

            if (fromIndex !== -1 && toIndex !== -1 && districtDragged && toDistrict && toDistrict.sortOrder) {
                setActiveDistricts(arrayMove(activeDistricts, fromIndex, toIndex));
                await setNewSortOrder(districtDragged, toDistrict.sortOrder);
            } else {
                isSortSavingRef.current = false;
                props.setDataIsLoading(false);
            }
        }
    };

    const sort = async (key) => {
        if (sortOrder.key === key) {
            setSortOrder({
                key: key,
                direction: sortOrder.direction === 'desc' ? 'asc' : 'desc'
            });
        } else {
            setSortOrder({
                key: key,
                direction: 'desc'
            });
        }
    };

    useEffect(() => {
        if (sortOrder.key) {
            fetchDistricts(sortOrder.key, sortOrder.direction);
        }
        // fetchDistricts är stabil från useCallback, men vi vill bara köra när sortOrder ändras
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sortOrder]);

    function nameChange(e) {
        const name = e.currentTarget.value;
        setSaveDialogState(prevState => ({
            ...prevState,
            name
        }));
    }

    function handleColorChange(e) {
        const color = e.currentTarget.value;
        setSaveDialogState(prevState => ({
            ...prevState,
            color
        }));
    }

    function handleInputColorChange(e) {
        const color = e.currentTarget.value;

        //validate color
        if (/^#[0-9A-F]{6}$/i.test(color)) {
            setSaveDialogState(prevState => ({
                ...prevState,
                color,
                textColor: color
            }));
        } else {
            setSaveDialogState(prevState => ({
                ...prevState,
                textColor: color === '' ? `#${color}` : color
            }));
        }
    }

    /**
     * Spara ett nytt distrikt (anropas från SaveButton när användaren klickar på spara)
     */
    async function saveSingleDistrictNew() {
        setSaveDialogState(prevState => ({ ...prevState, isSaving: true }));

        const { name, color, addWatcher, emptyDistrict } = saveDialogState;

        try {
            const result = await mapService.saveDistrict({
                ...emptyDistrict,
                type: props.type,
                name, 
                color, 
                addWatcher,
                spatialGeoJson: emptyDistrict.spatialGeoJson ? JSON.stringify(emptyDistrict.spatialGeoJson) : '',
                homeMarkerGeoJson: emptyDistrict.homeMarkerGeoJson ? JSON.stringify([emptyDistrict.homeMarkerGeoJson]) : ''
            }, emptyDistrict.id);
            
            const salesDistrictScenarioId = result.salesDistrictScenarioId
                || emptyDistrict.salesDistrictScenarioId
                || params.scenarioId
                || (props.match && props.match.params && props.match.params.scenarioId)
                || (props.scenarios && props.scenarios.length > 0 && props.scenarios[0].salesDistrictScenarioId);
            const path = `${props.type === 'foodservice' ? `/foodservice-scenario` : `/scenario`}/${salesDistrictScenarioId}/create/${result.id}`;
            
            // Uppdatera state för att visa att distriktet sparades
            setSaveDialogState(prevState => ({ 
                ...prevState,
                isSaving: false,
                isSaved: true
            }));
            
            // Hård navigering för att säkerställa att kartan initieras korrekt
            // (När man skapar distrikt från listan behöver vi en fullständig omladdning)
            // Dialogen stängs automatiskt när sidan laddas om
            window.location.href = path;
        } catch (error) {
            console.error('[ActiveDistricts] Error saving new district:', error);
            if (isMountedRef.current) {
                setSaveDialogState(prevState => ({ 
                    ...prevState,
                    isSaving: false
                }));
            }
            alert('Kunde inte spara distrikt. Försök igen.');
        }
    }

    async function toggleSaveDialog() {
        if (!saveDialogState.isSaveDialogOpen) {
            // Rensa state för nytt distrikt
            setSaveDialogState({
                ...EMPTY_STATE,
                isSaveDialogOpen: true,
                isLoading: true
            });

            try {
                // Steg 1: Hämta tomt distrikt från API
                const salesDistrictScenarioId = params.scenarioId
                    || (props.match && props.match.params && props.match.params.scenarioId)
                    || (props.scenarios && props.scenarios.length > 0 && props.scenarios[0].salesDistrictScenarioId)
                    || null;
                const emptyDistrict = await mapService.createDistrict(props.type, salesDistrictScenarioId);
                
                // Steg 2: Föreslå färg från befintliga distrikt
                let color = '#ff0000'; // Default färg
                if (activeDistricts && activeDistricts.length > 0) {
                    const excludeColors = activeDistricts.map(district => {
                        return district.color || '#ff0000';
                    });
                    color = suggestColor(excludeColors);
                }

                setSaveDialogState(prevState => ({
                    ...prevState,
                    emptyDistrict,
                    isLoading: false,
                    color,
                    textColor: color
                }));
            } catch (error) {
                console.error('Error creating empty district:', error);
                setSaveDialogState(prevState => ({
                    ...prevState,
                    isLoading: false
                }));
                alert('Kunde inte skapa tomt distrikt. Försök igen.');
            }
        } else {
            setSaveDialogState(prevState => ({
                ...prevState,
                isSaveDialogOpen: !prevState.isSaveDialogOpen,
                isLoading: false
            }));
        }
    }    

    return (
        <div className={classNames({
            'districts__content': true,
            'districts__content--gray': true,
            'districts__content--first': true,
            'districts__content--min-height-small': true,
            'districts__content--is-loading': props.dataIsLoading
        })}>
            <div className="districts__content-fit">
                {activeDistricts !== null && activeDistricts.length > 0 &&
                    <>
                        <div className="districts__table-scroll-fade">
                            <DndContext 
                                sensors={sensors}
                                collisionDetection={closestCenter}
                                onDragStart={handleDragStart}
                                onDragEnd={handleDragEnd}>
                                <div className="districts__table-scroll">
                                    <table className="table table--striped table--num table--border-bottom">
                                        <thead>
                                            <tr>
                                                <th className="table__text"><SortButton {...props} sortkey="sortorder" sortOrder={sortOrder} onClick={() => {
                                                    setSortOrder({
                                                        key: 'sortorder',
                                                        direction: 'asc'
                                                    });
                                                }} modifiers={{
                                                    'sort-button--no-text': true, 
                                                    'sort-button--default-sortorder': true,
                                                    'sort-button--default-sortorder-is-active': sortOrder.key === 'sortorder'
                                                    }}></SortButton></th>
                                                <th className="table__text"><SortButton {...props} sortkey="name" sortOrder={sortOrder} onClick={(key) => sort(key)}>SÄLJDISTRIKT</SortButton></th>
                                                <th><SortButton {...props} sortkey="numberofhits" sortOrder={sortOrder}  onClick={(key) => sort(key)}>Enheter</SortButton></th>
                                                <th className="districts__table-unit-toggler-th">Visa enheter på karta</th>
                                                <th><SortButton {...props} sortkey="salesDistrictArea" sortOrder={sortOrder}  onClick={(key) => sort(key)}>Area <span className="table__light-header">(km2)</span></SortButton></th>
                                                {props.type !== 'foodservice' &&
                                                    <>
                                                        <th><SortButton {...props} sortkey="turnover" sortOrder={sortOrder}  onClick={(key) => sort(key)}>Omsättning <span className="table__light-header">(KKR)</span></SortButton></th>
                                                        <th><SortButton {...props} sortkey="frequency" sortOrder={sortOrder}  onClick={(key) => sort(key)}>Frekvens</SortButton></th>
                                                        <th><SortButton {...props} sortkey="vistingtimehours" sortOrder={sortOrder}  onClick={(key) => sort(key)}>Besökstid <span className="table__light-header">(H)</span></SortButton></th>
                                                    </>
                                                }
                                                <th className="table__text"><SortButton {...props} sortkey="lastupdated" sortOrder={sortOrder}  onClick={(key) => sort(key)}>UPPDATERAD</SortButton></th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <SortableContext 
                                                items={activeDistricts.filter(d => d && d.id)}
                                                strategy={verticalListSortingStrategy}>
                                                {activeDistricts && activeDistricts.filter(d => d && d.id).map(district => {
                                                    return (
                                                        <SortableItem key={district.id} 
                                                                      id={district.id} 
                                                                      dataIsLoading={props.dataIsLoading}
                                                                      sortOrder={sortOrder}
                                                                      scenarios={props.scenarios}
                                                                      type={props.type}
                                                                      district={district}
                                                                      handleTogglePins={(checked) => props.handleTogglePins(district, checked)}>
                                                        </SortableItem>
                                                    );
                                                })}
                                            </SortableContext>
                                            {activeDistricts &&
                                                <tr className="tfoot">
                                                    <td colSpan={2} className="table__text">Totalt</td>
                                                    <td>{props.total('numberOfHits').toLocaleString('sv')}</td>
                                                    <td>
                                                        <TogglePins checked={activeDistricts.filter(f => f.showPins === true).length === activeDistricts.length} onChange={() => props.handleToggleAllPins()} />
                                                    </td>
                                                    <td>{props.total('salesDistrictArea').toLocaleString('sv')}</td>
                                                    {props.type !== 'foodservice' &&
                                                        <td>{props.total('turnOver').toLocaleString('sv')}</td>
                                                    }
                                                    <td colSpan={props.type === 'foodservice' ? 2 : 5}></td>
                                                </tr>
                                            }          
                                        </tbody>
                                    </table>
                                </div>
                                <DragOverlay
                                    wrapperElement="table"
                                    className="table table--striped table--num table--border-bottom">
                                    {activeId ? (() => {
                                        const draggedDistrict = activeDistricts.find(item => item && item.id === activeId);
                                        return draggedDistrict ? (
                                            <tbody>
                                                <Item
                                                    id={activeId}
                                                    district={draggedDistrict}
                                                    dataIsLoading={props.dataIsLoading}
                                                    sortOrder={sortOrder}
                                                    scenarios={props.scenarios}
                                                    type={props.type}
                                                    handleTogglePins={() => {}}
                                                />
                                            </tbody>
                                        ) : null;
                                    })() : null}
                                </DragOverlay>
                            </DndContext>
                        </div>
                        <div className="districts__create-toolbar">
                            <button type="button" 
                                    className="form__button districts__add"
                                    onClick={() => toggleSaveDialog()}>
                                + Nytt säljdistrikt
                            </button>
                            <Import units={[]} 
                                    scenarios={props.scenarios} 
                                    type={props.type} />
                        </div>
                    </>
                }

                {!props.mapIsLoading && activeDistricts && activeDistricts.length === 0 &&
                    <div className="districts__no-hits">
                        <p>👋 Hej! Du har inga säljdistrikt än. Skapa ett nytt distrikt genom att trycka på knappen nedan. 💫🌍</p>
                        <div className="districts__create-toolbar">
                            <button type="button" 
                                    className="form__button districts__add"
                                    onClick={() => toggleSaveDialog()}>
                                + Nytt säljdistrikt
                            </button>
                            <Import units={[]} 
                                    scenarios={props.scenarios} 
                                    type={props.type} />
                        </div>
                    </div>
                }
            </div>
            <Takeover isOpen={saveDialogState.isSaveDialogOpen} toggleTakeover={() => toggleSaveDialog()} heading="Skapa nytt säljdistrikt">
                <div className={classNames({
                    'save-district': true,
                    'save-district--is-saving': saveDialogState.isSaving,
                    'save-district--is-loading': saveDialogState.isLoading
                })}>
                    {saveDialogState.isSaveDialogOpen &&
                       <div className="save-district__content-outer">
                            <div className="save-district__content">
                                <label className="create-district-map__label">Namn</label>
                                <input className="create-district-map__input" type="text" onChange={(e) => nameChange(e)} value={saveDialogState.name} />
                                <label className="create-district-map__label" htmlFor="districtcolor">Färg på området i kartan, eller välj en egen</label>
                                <ColorPicker color={saveDialogState.color} 
                                            textColor={saveDialogState.textColor} 
                                            onChange={(e) => handleColorChange(e)} 
                                            handleInputColorChange={(e) => handleInputColorChange(e)} 
                                            salesDistrictScenarioId={params.scenarioId || (props.match && props.match.params && props.match.params.scenarioId)} />
                            </div>
                            <div className="create-district-map__forms-footer">
                                <SaveButton onClick={() => saveSingleDistrictNew()} 
                                            disabled={saveDialogState.isSaving || !saveDialogState.name || !saveDialogState.color || saveDialogState.isLoading}
                                            isLoading={saveDialogState.isSaving}
                                            isSaved={saveDialogState.isSaved} 
                                            text="Spara & visa distriktet" 
                                            textSaving="Sparar distriktet..."
                                            textSaved="Distriktet sparades!"
                                            modifiers={{
                                                'create-district-map__create': true,
                                                'report-save-btn--centered': true
                                            }} />
                                <Button type="button" 
                                    modifiers={{
                                        'form__button--muted': true,
                                        'form__button--centered': true
                                    }}
                                    onClick={() => toggleSaveDialog()}>Avbryt</Button>
                            </div>
                        </div>
                    }
                    <SpinnerDots />
                </div>
            </Takeover>
        </div>
    );
};

export default withAuthProvider(ActiveDistricts);